import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { useRef, useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  EventHeldStatus,
  EventTicketCardFragmentDoc,
  MyPreSaleEventConnectionFragment,
  MyPreSaleEventConnectionFragmentDoc,
  MyTicketsQuery,
  MyTicketsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const [filter, setFilter] = useState<EventHeldStatus>(EventHeldStatus.All)
  const myPreSaleEventsBeforeFetchRef =
    useRef<MyPreSaleEventConnectionFragment>()

  const { isFirstLoadRef } = useGetNewestData<
    MyTicketsQuery,
    MyTicketsQueryVariables
  >({
    query: QUERY,
    variables: { filter: EventHeldStatus.All, skipUser: true },
    fields: [
      {
        fieldName: 'myPreSaleEvents',
        fragmentName: 'EventTicketCard',
        fragment: EventTicketCardFragmentDoc,
      },
    ],
  })

  const { data, fetchMore, loading, refetch, networkStatus } = useQuery<
    MyTicketsQuery,
    MyTicketsQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { filter: EventHeldStatus.All, skipUser: false },
    async onCompleted({ myPreSaleEvents }) {
      isFirstLoadRef.current = false
      if (
        (!myPreSaleEvents?.edges?.length ||
          myPreSaleEventsBeforeFetchRef.current?.edges?.length ===
            myPreSaleEvents.edges.length) &&
        myPreSaleEvents?.pageInfo.hasNextPage
      ) {
        await fetchMore({
          variables: {
            filter,
            pageInput: { cursor: myPreSaleEvents.pageInfo.endCursor },
          },
        })
      }
      myPreSaleEventsBeforeFetchRef.current = myPreSaleEvents
    },
  })

  const isNextFetch = data?.myPreSaleEvents?.edges !== undefined

  return {
    state: {
      loading: loading && !isNextFetch,
      fetching:
        networkStatus === NetworkStatus.fetchMore ||
        networkStatus === NetworkStatus.setVariables,
      events: data?.myPreSaleEvents ?? null,
      hasNextPage: !!data?.myPreSaleEvents?.pageInfo.hasNextPage,
      user: data?.user ?? null,
      filter,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            filter,
            pageInput: {
              cursor: data?.myPreSaleEvents?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFilter(newFilter) {
        setFilter(newFilter)
        await refetch({ filter: newFilter })
      },
    },
  }
})

const QUERY = gql`
  query MyTickets(
    $filter: EventHeldStatus!
    $pageInput: PageInput
    $skipUser: Boolean!
  ) {
    myPreSaleEvents(filter: $filter, pageInput: $pageInput) {
      ...MyPreSaleEventConnection
    }
    user @skip(if: $skipUser) {
      id
      hasWaitingPaymentItems
      hasUnappliedDeliveryItems
      hasManagementTask @client
      unreadNotificationCount
      isMembershipPersonalInformationRequired
    }
  }
  ${MyPreSaleEventConnectionFragmentDoc}
`
